import React from 'react';
import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faExchangeAlt, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';

const DatosFactura = ({ emisor, receptor, facturaElectronica }) => {
    const getUsoCfdiStyle = (usoCfdi) => {
        const isSinEfectosFiscales = usoCfdi && usoCfdi.toUpperCase().includes('S01');
        return {
            color: isSinEfectosFiscales ? 'red' : '#28a745',
            fontWeight: 'bold',
            fontSize: '1.2em'
        };
    };

    const getFormaPagoStyle = (formaPago) => {
        if (!formaPago) return {};
        
        // Formas de pago electrónicas bancarizadas según catálogo SAT
        const formasElectronicas = ['03', '04', '28', '29', '05'];
        const codigo = formaPago.substring(0, 2);
        const isElectronica = formasElectronicas.includes(codigo);
        
        return {
            color: isElectronica ? '#28a745' : 'red',
            fontWeight: 'bold',
            fontSize: '1.1em'
        };
    };

    const getTipoComprobanteBadge = (tipoStr) => {
        if (!tipoStr) return null;
        
        const tipo = tipoStr.charAt(0).toUpperCase();
        
        const configs = {
            I: { label: 'Ingreso', variant: 'primary', icon: faArrowDown },
            E: { label: 'Egreso', variant: 'danger', icon: faArrowUp },
            T: { label: 'Traslado', variant: 'info', icon: faExchangeAlt },
            P: { label: 'Pago', variant: 'success', icon: faMoneyBillWave },
            N: { label: 'Nómina', variant: 'secondary', icon: faMoneyBillWave }
        };
        
        const config = configs[tipo] || { label: tipoStr, variant: 'secondary', icon: faMoneyBillWave };
        
        return (
            <Badge bg={config.variant} className="ms-2 px-2 py-1">
                <FontAwesomeIcon icon={config.icon} className="me-1" />
                {config.label}
            </Badge>
        );
    };

    return (
        <Row className="mb-4">
            <Col xs={12} md={6} className="mb-3 mb-md-0">
                <p className="text-dark mb-0">Datos del emisor</p>
                <p className="mb-0 fw-bold text-dark">{emisor?.Nombre}</p>
                <p className="mb-0">
                    <span className="text-dark">RFC:</span>
                    <span> {emisor?.Rfc}</span>
                </p>
                <p className="mb-3">
                    <span className="text-dark">Régimen:</span>
                    <span> {emisor?.vRegimenFiscalEmisor}</span>
                </p>

                <p className="text-dark mb-0">Datos del cliente</p>
                <p className="mb-0 fw-bold text-dark">{receptor?.Nombre}</p>
                <p className="mb-0">
                    <span className="text-dark">RFC:</span>
                    <span> {receptor?.Rfc}</span>
                </p>
                <p className="mb-0">
                    <span className="text-dark">USO CFDI: </span>
                    <span style={getUsoCfdiStyle(receptor?.vUsoCFDI)}>{receptor?.vUsoCFDI}</span>
                </p>
                <p className="mb-0">
                    <span className="text-dark">Régimen:</span>
                    <span> {receptor?.vRegimen}</span>
                </p>
                <p className="mb-0">
                    <span className="text-dark">Domicilio Fiscal:</span>
                    <span> {receptor?.DomicilioFiscal}</span>
                </p>
                {receptor?.NumRegIdTrib && (
                    <p className="mb-0">
                        <span className="text-dark">Número de Registro Tributario Extranjero:</span>
                        <span> {receptor.NumRegIdTrib}</span>
                    </p>
                )}
                {receptor?.ResidenciaFiscal && (
                    <p className="mb-0">
                        <span className="text-dark">Residencia Fiscal:</span>
                        <span> {receptor.ResidenciaFiscal}</span>
                    </p>
                )}
            </Col>

            <Col xs={12} md={6} className="text-md-end text-start">
                <h5 className="mb-3">FACTURA ELECTRÓNICA</h5>
                <p className="mb-0">
                    <span className="text-dark">Serie y Folio:</span>
                    <span style={{ color: 'red', fontWeight: 'bold', fontSize: '1.3em' }}> {facturaElectronica?.Serie} {facturaElectronica?.Folio}</span>
                </p>
                {facturaElectronica.MetodoPago &&
                    <p className="mb-0">
                        <span className="text-dark">Método de pago:</span>
                        <span> {facturaElectronica?.MetodoPago}</span>
                    </p>
                }
                {facturaElectronica.FormaPago &&
                    <p className="mb-0">
                        <span className="text-dark">Forma de pago:</span>
                        <span style={getFormaPagoStyle(facturaElectronica?.FormaPago)}> {facturaElectronica?.FormaPago}</span>
                    </p>
                }
                <p className="mb-0">
                    <span className="text-dark">Certificado del emisor:</span>
                    <span> {facturaElectronica?.CertificadoEmisor}</span>
                </p>
                <p className="mb-0">
                    <span className="text-dark">Fecha de emisión:</span>
                    <span> {facturaElectronica?.Fecha}</span>
                </p>
                <p className="mb-0">
                    <span className="text-dark">Lugar de expedición:</span>
                    <span> {facturaElectronica?.LugarExpedicion}</span>
                </p>
                <p className="mb-0 d-flex align-items-center justify-content-start justify-content-md-end">
                    <span className="text-dark">Tipo de comprobante:</span>
                    {getTipoComprobanteBadge(facturaElectronica?.TipoDeComprobante)}
                </p>
                <p className="mb-0">
                    <span className="text-dark">Moneda:</span>
                    <span> {facturaElectronica?.Moneda}</span>
                </p>
                {facturaElectronica?.TipoDeCambio && facturaElectronica.Moneda !== 'MXN' && (
                    <p className="mb-0">
                        <span className="text-dark">T.C:</span>
                        <span> {facturaElectronica.TipoDeCambio}</span>
                    </p>
                )}
                <p className="mb-0">
                    <span className="text-dark">Exportación:</span>
                    <span> {facturaElectronica?.Exportacion}</span>
                </p>
            </Col>
        </Row>
    );
};

DatosFactura.propTypes = {
    emisor: PropTypes.object,
    receptor: PropTypes.object,
    facturaElectronica: PropTypes.object
};

export default DatosFactura;